import type { FocusEvent, Ref } from 'react';
import React, {
    Children,
    useRef,
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { cloneWidget } from '@alfa-bank/site-util-create-widget/clone-widget';
import type { FieldInputProps, FieldMetaState } from 'react-final-form';
import { Field } from 'react-final-form';
import { validateNotEmpty } from '@alfa-bank/site-form-helpers/validation/not-empty';
import { isSuccess } from '@alfa-bank/site-form-helpers/meta/is-success';
import { isError } from '@alfa-bank/site-form-helpers/meta/is-error';
import type { TMeta } from '@alfa-bank/site-form-helpers/src/meta/models';
import { publish } from '@alfa-bank/site-event-bus';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { composeValidators } from '@alfa-bank/site-form-helpers/validation/utils';
import type { TValidatorValue } from '@alfa-bank/site-form-helpers/src/validation/models';

import type { TFields } from '../form/models';
import { clearUndefinedProps } from '../../helpers/clear-undefined-props';
import { fieldValidators } from '../../helpers';
import {
    useFormContext,
    useFieldSafe as useFormField,
    useFormStateSafe,
    useFormSafe,
} from '../form/context';

import type {
    IFieldInputProps,
    IOnBlurEventArgs,
    IOnChangeEventArgs,
    TOnFocus,
    IProps,
    TOnBlur,
    TOnChange,
    IOnFocusEvent,
} from './models';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const InputField = forwardRef((props: IProps, forwardedRef: Ref<unknown>) => {
    const {
        analytics = {},
        children,
        focusEvent,
        name = '',
        postValidator = () => undefined,
        required = false,
        type = 'default',
        onBlur: onBlurFromProps = () => {},
        getInputProps = () => ({}),
        isCaptionField,
        isErrorField,
    } = props;

    const { disabled } = useFormContext();
    const formApi = useFormSafe<TFields>();
    const initialValue = formApi?.getState().initialValues?.inputName;
    const formState = useFormStateSafe({
        subscription: { values: true },
    });
    const ref = useRef<HTMLInputElement>(null);
    const [backupName, setBackupName] = useState('');

    useEffect(() => {
        if (ref.current) setBackupName(ref.current.name);
        // выполняется только на монтирование
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleValidation = required ? validateNotEmpty() : undefined;

    const isErrorOnBlur = (
        input: FieldInputProps<TValidatorValue, HTMLElement>,
        meta: FieldMetaState<TValidatorValue>
    ) => !input.value && meta.error && meta.touched;

    const isErrorOnSubmit = (input: FieldInputProps<TValidatorValue, HTMLElement>) =>
        !input.value && formState?.values.error;

    useImperativeHandle(forwardedRef, () => ({
        focus: (params?: FocusOptions) => ref.current?.focus(params),
    }));

    const child = Children.only(children);
    /* istanbul ignore next */
    const { name: inputName = name, caption } = child.props;
    const {
        onError: analyticsOnErrorData,
        onFirstChange: analyticsOnFirstChange,
        onFocus: analyticsOnFocusData,
        onSuccess: analyticsOnSuccessData,
    } = analytics;

    /**
     * Если есть значение по-умолчанию, то при установке значения
     * не срабатывает валидация поля. Здесь мы тригерим валидацию
     * через событие change.
     */
    useEffect(() => {
        if (initialValue) formApi?.change(inputName, initialValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    const [changed, setChanged] = useState<boolean>(false);

    const validator = fieldValidators[type];

    const resultValidator = required
        ? composeValidators(fieldValidators.required, validator, postValidator)
        : composeValidators(validator, postValidator);

    const field = useFormField(inputName || backupName, {
        validate: resultValidator,
    });

    const fieldMeta = field?.meta as TMeta;

    useEffect(() => {
        if (fieldMeta?.error && fieldMeta?.touched && analyticsOnErrorData) {
            sendEvent({
                ...analyticsOnErrorData,
                label_detail: fieldMeta.error,
            });
        }
    }, [analyticsOnErrorData, fieldMeta?.error, fieldMeta?.touched]);

    /* istanbul ignore next */
    const handleBlur = (e: FocusEvent<HTMLElement, Element>) => {
        /* istanbul ignore next */
        field?.input.onBlur(e);

        /* istanbul ignore if */
        if (focusEvent) {
            publish(focusEvent, false);
        }
        onBlurFromProps(field, e);
    };

    const handleFocus = (e: FocusEvent<HTMLElement, Element>) => {
        if (analyticsOnFocusData) {
            sendEvent(analyticsOnFocusData);
        }
        field?.input.onFocus(e);
    };

    const handleChange = ({ event, value }: IOnChangeEventArgs) => {
        field?.input.onChange(value);

        if (!changed && analyticsOnFirstChange) {
            sendEvent(analyticsOnFirstChange);
            setChanged(true);
        }

        const fieldState = formApi?.getFieldState(field?.input.name || '');
        /* istanbul ignore next */
        if (fieldState?.valid) {
            fieldState.blur();
        }
        /* Вызываем onBlur для вызова валидации, если значение выбрано из списка */
        if (event?.type === 'click') {
            field?.input.onBlur();
        }
    };

    const fieldValue = formState?.values[inputName || backupName] || '';

    const isCaption = ({ input, meta }: IFieldInputProps) => {
        if (field?.meta.active) return caption;

        return isError(fieldMeta) || isErrorOnBlur(input, meta) || isErrorOnSubmit(input)
            ? field?.meta.error
            : caption;
    };

    const isInputError = ({ input, meta }: IFieldInputProps) => {
        if (field?.meta.active) return false;
        if (isSuccess(fieldMeta)) return false;

        return (
            (!!isError(fieldMeta) && field?.meta.error) ||
            isErrorOnBlur(input, meta) ||
            isErrorOnSubmit(input)
        );
    };

    const onBlur: TOnBlur = (input, { event, value }) => {
        handleBlur(event);
        if (value) {
            input.onChange(value.trim());
        }
        input.onBlur(event);
    };

    const getSuccessStatus = () => {
        if (field?.meta.active) return false;
        // не отображать признак валидного поля, если поле необязательное и при этом пустое
        if (!required && !fieldValue) return false;

        return isSuccess(fieldMeta);
    };

    const isSuccessField = getSuccessStatus();

    useEffect(() => {
        if (!isSuccessField || !analyticsOnSuccessData) return;
        sendEvent(analyticsOnSuccessData);
    }, [analyticsOnSuccessData, isSuccessField]);

    const onChange: TOnChange = (input, { event, value }) => {
        handleChange({ event, value });
        input.onChange(value);
    };

    const onFocus: TOnFocus = (input, { event }) => {
        handleFocus(event);
        input.onFocus(event);
    };

    /** Хелпер отрабатывающий при нажатии на крести при невалидном поле */
    const handleCancel = () => {
        formApi?.change(inputName || backupName, '');
    };

    return formApi ? (
        <Field name={inputName || backupName} validate={handleValidation}>
            {({ input, meta }) =>
                cloneWidget(children, {
                    ...input,
                    ...field?.input,
                    forwardedRef: ref,
                    // При disabled не отображаем состояние поля
                    ...(disabled
                        ? {}
                        : {
                              caption: isCaptionField
                                  ? isCaptionField({ input, meta })
                                  : isCaption({ input, meta }),
                              success: isSuccessField,
                              error: isErrorField
                                  ? isErrorField({ input, meta })
                                  : isInputError({ input, meta }),
                          }),
                    value: fieldValue,
                    onBlur: ({ event, value }: IOnBlurEventArgs) => onBlur(input, { event, value }),
                    onCancel: handleCancel,
                    onChange: ({ event, value }: IOnChangeEventArgs) =>
                        onChange(input, { event, value }),
                    onFocus: ({ event }: IOnFocusEvent) => onFocus(input, { event }),
                    ...getInputProps(field),
                    ...clearUndefinedProps({ disabled }),
                })
            }
        </Field>
    ) : (
        cloneWidget(children)
    );
});
